/**
 * AMF0/AMF3 Encoder/Decoder for browser
 * 
 * AMF (Action Message Format) is a binary format used by Flash/Flex
 * to communicate with server-side components via AMFphp.
 * 
 * This implementation supports both AMF0 and AMF3 types.
 */

// AMF0 Type markers
const AMF0_NUMBER = 0x00;
const AMF0_BOOLEAN = 0x01;
const AMF0_STRING = 0x02;
const AMF0_OBJECT = 0x03;
const AMF0_MOVIECLIP = 0x04; // Reserved, not used
const AMF0_NULL = 0x05;
const AMF0_UNDEFINED = 0x06;
const AMF0_REFERENCE = 0x07;
const AMF0_ECMA_ARRAY = 0x08;
const AMF0_OBJECT_END = 0x09;
const AMF0_STRICT_ARRAY = 0x0A;
const AMF0_DATE = 0x0B;
const AMF0_LONG_STRING = 0x0C;
const AMF0_UNSUPPORTED = 0x0D;
const AMF0_RECORDSET = 0x0E; // Reserved, not used
const AMF0_XML_DOC = 0x0F;
const AMF0_TYPED_OBJECT = 0x10;
const AMF0_AVMPLUS = 0x11; // Switch to AMF3

// AMF3 Type markers
const AMF3_UNDEFINED = 0x00;
const AMF3_NULL = 0x01;
const AMF3_FALSE = 0x02;
const AMF3_TRUE = 0x03;
const AMF3_INTEGER = 0x04;
const AMF3_DOUBLE = 0x05;
const AMF3_STRING = 0x06;
const AMF3_XML_DOC = 0x07;
const AMF3_DATE = 0x08;
const AMF3_ARRAY = 0x09;
const AMF3_OBJECT = 0x0A;
const AMF3_XML = 0x0B;
const AMF3_BYTE_ARRAY = 0x0C;

export class AMF0Encoder {
  private buffer: number[] = [];
  
  encode(value: any): Uint8Array {
    this.buffer = [];
    this.writeValue(value);
    return new Uint8Array(this.buffer);
  }

  private writeValue(value: any): void {
    if (value === null) {
      this.writeNull();
    } else if (value === undefined) {
      this.writeUndefined();
    } else if (typeof value === 'boolean') {
      this.writeBoolean(value);
    } else if (typeof value === 'number') {
      this.writeNumber(value);
    } else if (typeof value === 'string') {
      this.writeString(value);
    } else if (value instanceof Date) {
      this.writeDate(value);
    } else if (Array.isArray(value)) {
      this.writeArray(value);
    } else if (typeof value === 'object') {
      this.writeObject(value);
    } else {
      this.writeNull();
    }
  }

  private writeByte(byte: number): void {
    this.buffer.push(byte & 0xFF);
  }

  private writeBytes(bytes: number[]): void {
    this.buffer.push(...bytes);
  }

  private writeNull(): void {
    this.writeByte(AMF0_NULL);
  }

  private writeUndefined(): void {
    this.writeByte(AMF0_UNDEFINED);
  }

  private writeBoolean(value: boolean): void {
    this.writeByte(AMF0_BOOLEAN);
    this.writeByte(value ? 1 : 0);
  }

  private writeNumber(value: number): void {
    // Log NaN values for debugging with stack trace
    if (Number.isNaN(value)) {
      console.log('[AMF Encoder] Writing NaN value');
      console.trace('[AMF Encoder] NaN stack trace');
    }
    this.writeByte(AMF0_NUMBER);
    this.writeDouble(value);
  }

  private writeDouble(value: number): void {
    const buffer = new ArrayBuffer(8);
    const view = new DataView(buffer);
    view.setFloat64(0, value, false); // big endian
    for (let i = 0; i < 8; i++) {
      this.writeByte(view.getUint8(i));
    }
  }

  private writeString(value: string): void {
    const utf8 = this.stringToUtf8(value);
    if (utf8.length < 65536) {
      this.writeByte(AMF0_STRING);
      this.writeU16(utf8.length);
      this.writeBytes(utf8);
    } else {
      this.writeByte(AMF0_LONG_STRING);
      this.writeU32(utf8.length);
      this.writeBytes(utf8);
    }
  }

  private writeU16(value: number): void {
    this.writeByte((value >> 8) & 0xFF);
    this.writeByte(value & 0xFF);
  }

  private writeU32(value: number): void {
    this.writeByte((value >> 24) & 0xFF);
    this.writeByte((value >> 16) & 0xFF);
    this.writeByte((value >> 8) & 0xFF);
    this.writeByte(value & 0xFF);
  }

  private writeDate(value: Date): void {
    this.writeByte(AMF0_DATE);
    this.writeDouble(value.getTime());
    this.writeU16(0); // timezone offset (not used)
  }

  private writeArray(value: any[]): void {
    this.writeByte(AMF0_STRICT_ARRAY);
    this.writeU32(value.length);
    for (const item of value) {
      this.writeValue(item);
    }
  }

  private writeObject(value: object): void {
    // Check if it has a _explicitType property (typed object)
    const explicitType = (value as any)._explicitType;
    
    if (explicitType) {
      this.writeByte(AMF0_TYPED_OBJECT);
      const typeBytes = this.stringToUtf8(explicitType);
      this.writeU16(typeBytes.length);
      this.writeBytes(typeBytes);
    } else {
      this.writeByte(AMF0_OBJECT);
    }

    // Write key-value pairs
    for (const [key, val] of Object.entries(value)) {
      if (key === '_explicitType') continue; // skip type marker
      
      const keyBytes = this.stringToUtf8(key);
      this.writeU16(keyBytes.length);
      this.writeBytes(keyBytes);
      this.writeValue(val);
    }

    // End marker
    this.writeU16(0); // empty string
    this.writeByte(AMF0_OBJECT_END);
  }

  private stringToUtf8(str: string): number[] {
    const bytes: number[] = [];
    for (let i = 0; i < str.length; i++) {
      const charCode = str.charCodeAt(i);
      if (charCode < 0x80) {
        bytes.push(charCode);
      } else if (charCode < 0x800) {
        bytes.push(0xC0 | (charCode >> 6));
        bytes.push(0x80 | (charCode & 0x3F));
      } else if (charCode < 0x10000) {
        bytes.push(0xE0 | (charCode >> 12));
        bytes.push(0x80 | ((charCode >> 6) & 0x3F));
        bytes.push(0x80 | (charCode & 0x3F));
      } else {
        bytes.push(0xF0 | (charCode >> 18));
        bytes.push(0x80 | ((charCode >> 12) & 0x3F));
        bytes.push(0x80 | ((charCode >> 6) & 0x3F));
        bytes.push(0x80 | (charCode & 0x3F));
      }
    }
    return bytes;
  }
}

export class AMF0Decoder {
  private data!: DataView;
  private offset = 0;
  private amf0ObjectRefs: any[] = [];  // Track AMF0 object references
  private amf3StringRefs: string[] = [];
  private amf3ObjectRefs: any[] = [];
  private amf3TraitRefs: any[] = [];
  private amf3Mode = false;

  /**
   * Decode AMF0 data (default for server responses)
   */
  decode(buffer: ArrayBuffer): any {
    this.data = new DataView(buffer);
    this.offset = 0;
    this.amf0ObjectRefs = [];
    this.amf3StringRefs = [];
    this.amf3ObjectRefs = [];
    this.amf3TraitRefs = [];
    this.amf3Mode = false;
    
    return this.readValue();
  }

  /**
   * Decode AMF3 data directly (for files like countries.db)
   */
  decodeAMF3(buffer: ArrayBuffer): any {
    this.data = new DataView(buffer);
    this.offset = 0;
    this.amf0ObjectRefs = [];
    this.amf3StringRefs = [];
    this.amf3ObjectRefs = [];
    this.amf3TraitRefs = [];
    this.amf3Mode = true;
    
    return this.readAMF3Value();
  }

  private readValue(): any {
    const type = this.readByte();
    switch (type) {
      case AMF0_NUMBER:
        return this.readDouble();
      case AMF0_BOOLEAN:
        return this.readByte() !== 0;
      case AMF0_STRING:
        return this.readString();
      case AMF0_OBJECT:
        return this.readObject();
      case AMF0_NULL:
        return null;
      case AMF0_UNDEFINED:
        return undefined;
      case AMF0_REFERENCE:
        return this.readReference();
      case AMF0_ECMA_ARRAY:
        return this.readEcmaArray();
      case AMF0_STRICT_ARRAY:
        return this.readStrictArray();
      case AMF0_DATE:
        return this.readDate();
      case AMF0_LONG_STRING:
        return this.readLongString();
      case AMF0_XML_DOC:
        return this.readXmlDoc();
      case AMF0_TYPED_OBJECT:
        return this.readTypedObject();
      case AMF0_AVMPLUS:
        // Switch to AMF3 mode
        return this.readAMF3Value();
      default:
        throw new Error(`Unknown AMF0 type: ${type}`);
    }
  }

  // ============ AMF3 Methods ============
  
  private readAMF3Value(): any {
    const type = this.readByte();
    switch (type) {
      case AMF3_UNDEFINED:
        return undefined;
      case AMF3_NULL:
        return null;
      case AMF3_FALSE:
        return false;
      case AMF3_TRUE:
        return true;
      case AMF3_INTEGER:
        return this.readAMF3Int();
      case AMF3_DOUBLE:
        return this.readDouble();
      case AMF3_STRING:
        return this.readAMF3String();
      case AMF3_DATE:
        return this.readAMF3Date();
      case AMF3_ARRAY:
        return this.readAMF3Array();
      case AMF3_OBJECT:
        return this.readAMF3Object();
      case AMF3_XML_DOC:
      case AMF3_XML:
        return this.readAMF3Xml();
      case AMF3_BYTE_ARRAY:
        return this.readAMF3ByteArray();
      default:
        throw new Error(`Unknown AMF3 type: ${type}`);
    }
  }

  private readAMF3Int(): number {
    let result = 0;
    let byte = this.readByte();
    
    if (byte < 0x80) {
      return byte;
    }
    
    result = (byte & 0x7F) << 7;
    byte = this.readByte();
    
    if (byte < 0x80) {
      return result | byte;
    }
    
    result = (result | (byte & 0x7F)) << 7;
    byte = this.readByte();
    
    if (byte < 0x80) {
      return result | byte;
    }
    
    result = (result | (byte & 0x7F)) << 8;
    byte = this.readByte();
    result |= byte;
    
    // Sign extend if needed (AMF3 integers are 29-bit signed)
    if (result >= 0x10000000) {
      result -= 0x20000000;
    }
    
    return result;
  }

  private readAMF3String(): string {
    const ref = this.readAMF3Int();
    
    // If low bit is 0, it's a reference
    if ((ref & 1) === 0) {
      const index = ref >> 1;
      return this.amf3StringRefs[index] || '';
    }
    
    // Otherwise, it's an inline string
    const length = ref >> 1;
    if (length === 0) {
      return '';
    }
    
    const str = this.readUtf8(length);
    this.amf3StringRefs.push(str);
    return str;
  }

  private readAMF3Date(): Date {
    const ref = this.readAMF3Int();
    
    // If low bit is 0, it's a reference
    if ((ref & 1) === 0) {
      const index = ref >> 1;
      return this.amf3ObjectRefs[index] as Date;
    }
    
    const timestamp = this.readDouble();
    const date = new Date(timestamp);
    this.amf3ObjectRefs.push(date);
    return date;
  }

  private readAMF3Array(): any[] {
    const ref = this.readAMF3Int();
    
    // If low bit is 0, it's a reference
    if ((ref & 1) === 0) {
      const index = ref >> 1;
      return this.amf3ObjectRefs[index] as any[];
    }
    
    const denseCount = ref >> 1;
    const arr: any[] = [];
    this.amf3ObjectRefs.push(arr);
    
    // Read associative portion (key-value pairs until empty string)
    let key = this.readAMF3String();
    while (key !== '') {
      (arr as any)[key] = this.readAMF3Value();
      key = this.readAMF3String();
    }
    
    // Read dense portion
    for (let i = 0; i < denseCount; i++) {
      arr.push(this.readAMF3Value());
    }
    
    return arr;
  }

  private readAMF3Object(): any {
    const ref = this.readAMF3Int();
    
    // If low bit is 0, it's a reference
    if ((ref & 1) === 0) {
      const index = ref >> 1;
      return this.amf3ObjectRefs[index];
    }
    
    // Read traits
    let traits: { className: string; dynamic: boolean; externalizable: boolean; properties: string[] };
    
    if ((ref & 2) === 0) {
      // Traits reference
      const traitsIndex = ref >> 2;
      traits = this.amf3TraitRefs[traitsIndex];
    } else {
      // Inline traits
      const externalizable = (ref & 4) !== 0;
      const dynamic = (ref & 8) !== 0;
      const propertyCount = ref >> 4;
      
      const className = this.readAMF3String();
      
      const properties: string[] = [];
      if (!externalizable) {
        for (let i = 0; i < propertyCount; i++) {
          properties.push(this.readAMF3String());
        }
      }
      
      traits = { className, dynamic, externalizable, properties };
      this.amf3TraitRefs.push(traits);
    }
    
    // Handle externalizable objects (like ArrayCollection)
    if (traits.externalizable) {
      // For ArrayCollection, the external data is an array
      if (traits.className === 'flex.messaging.io.ArrayCollection' || 
          traits.className === 'flex.messaging.io.ObjectProxy') {
        const arr = this.readAMF3Value();
        this.amf3ObjectRefs.push(arr);
        return arr;
      }
      // For other externalizable types, try to read as a single value
      const value = this.readAMF3Value();
      this.amf3ObjectRefs.push(value);
      return value;
    }
    
    // Create object
    const obj: any = {};
    if (traits.className) {
      obj._explicitType = traits.className;
    }
    this.amf3ObjectRefs.push(obj);
    
    // Read sealed properties
    for (const prop of traits.properties) {
      obj[prop] = this.readAMF3Value();
    }
    
    // Read dynamic properties if dynamic
    if (traits.dynamic) {
      let key = this.readAMF3String();
      while (key !== '') {
        obj[key] = this.readAMF3Value();
        key = this.readAMF3String();
      }
    }
    
    return obj;
  }

  private readAMF3Xml(): string {
    const ref = this.readAMF3Int();
    
    if ((ref & 1) === 0) {
      const index = ref >> 1;
      return this.amf3ObjectRefs[index] as string;
    }
    
    const length = ref >> 1;
    const xml = this.readUtf8(length);
    this.amf3ObjectRefs.push(xml);
    return xml;
  }

  private readAMF3ByteArray(): Uint8Array {
    const ref = this.readAMF3Int();
    
    if ((ref & 1) === 0) {
      const index = ref >> 1;
      return this.amf3ObjectRefs[index] as Uint8Array;
    }
    
    const length = ref >> 1;
    const bytes = new Uint8Array(this.data.buffer, this.offset, length);
    this.offset += length;
    this.amf3ObjectRefs.push(bytes);
    return bytes;
  }

  // ============ AMF0 Methods ============

  private readByte(): number {
    return this.data.getUint8(this.offset++);
  }

  private readU16(): number {
    const value = this.data.getUint16(this.offset, false);
    this.offset += 2;
    return value;
  }

  private readU32(): number {
    const value = this.data.getUint32(this.offset, false);
    this.offset += 4;
    return value;
  }

  private readDouble(): number {
    const value = this.data.getFloat64(this.offset, false);
    this.offset += 8;
    return value;
  }

  private readString(): string {
    const length = this.readU16();
    return this.readUtf8(length);
  }

  private readLongString(): string {
    const length = this.readU32();
    return this.readUtf8(length);
  }

  private readReference(): any {
    // AMF0 Reference - index to previously serialized object
    const index = this.readU16();
    if (index < this.amf0ObjectRefs.length) {
      return this.amf0ObjectRefs[index];
    }
    console.warn(`AMF0 Reference index ${index} out of bounds (${this.amf0ObjectRefs.length} refs)`);
    return null;
  }

  private readXmlDoc(): string {
    // AMF0 XML Document - same as long string
    const length = this.readU32();
    return this.readUtf8(length);
  }

  private readUtf8(length: number): string {
    // Validate that we have enough data
    if (length < 0) {
      console.warn(`AMF0 Invalid UTF8 length: ${length}`);
      return '';
    }
    
    const available = this.data.byteLength - this.offset;
    if (length > available) {
      console.warn(`AMF0 UTF8 length ${length} exceeds available data ${available} at offset ${this.offset}`);
      // Read what we can
      const actualLength = Math.max(0, available);
      if (actualLength === 0) return '';
      const bytes = new Uint8Array(this.data.buffer, this.offset, actualLength);
      this.offset += actualLength;
      return new TextDecoder('utf-8').decode(bytes);
    }
    
    const bytes = new Uint8Array(this.data.buffer, this.offset, length);
    this.offset += length;
    return new TextDecoder('utf-8').decode(bytes);
  }

  private readDate(): Date {
    const timestamp = this.readDouble();
    this.readU16(); // timezone (ignored)
    const date = new Date(timestamp);
    this.amf0ObjectRefs.push(date);  // Register reference
    return date;
  }

  private readObject(): object {
    const obj: any = {};
    this.amf0ObjectRefs.push(obj);  // Register reference BEFORE reading properties
    while (true) {
      const key = this.readString();
      if (key === '' && this.data.getUint8(this.offset) === AMF0_OBJECT_END) {
        this.offset++; // skip end marker
        break;
      }
      obj[key] = this.readValue();
    }
    return obj;
  }

  private readTypedObject(): object {
    const typeName = this.readString();
    const obj: any = { _explicitType: typeName };
    this.amf0ObjectRefs.push(obj);  // Register reference BEFORE reading properties
    while (true) {
      const key = this.readString();
      if (key === '' && this.data.getUint8(this.offset) === AMF0_OBJECT_END) {
        this.offset++; // skip end marker
        break;
      }
      obj[key] = this.readValue();
    }
    return obj;
  }

  private readEcmaArray(): object {
    const count = this.readU32(); // associative count (may not be accurate)
    const obj: any = {};
    this.amf0ObjectRefs.push(obj);  // Register reference BEFORE reading properties
    while (true) {
      const key = this.readString();
      if (key === '' && this.data.getUint8(this.offset) === AMF0_OBJECT_END) {
        this.offset++; // skip end marker
        break;
      }
      obj[key] = this.readValue();
    }
    return obj;
  }

  private readStrictArray(): any[] {
    const count = this.readU32();
    const arr: any[] = [];
    this.amf0ObjectRefs.push(arr);  // Register reference BEFORE reading elements
    for (let i = 0; i < count; i++) {
      arr.push(this.readValue());
    }
    return arr;
  }
}
